import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Campaign Health Overview Widget for Founder Dashboard
export const CampaignHealthWidget = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [atRisk, scale, campaigns] = await Promise.all([
          axios.get(`${API}/performance/campaigns/at-risk`),
          axios.get(`${API}/performance/campaigns/scale-candidates`),
          axios.get(`${API}/performance/campaigns`),
        ]);
        setData({
          atRisk: atRisk.data.count || 0,
          scaleCandidates: scale.data.count || 0,
          active: campaigns.data.length || 0,
        });
      } catch (e) {
        console.error('Failed to fetch campaign health:', e);
      } finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
      <div className="flex items-center justify-center h-24">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-emerald-500" />
      </div>
    </div>
  );

  if (!data) return null;

  return (
    <div
      className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 cursor-pointer hover:border-emerald-500/30 transition-colors"
      onClick={() => navigate('/campaign-health')}
      data-testid="campaign-health-widget"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Campaign Health</h3>
        <span className="px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded">Phase 4</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="text-center p-3 bg-red-500/10 rounded-lg border border-red-500/20">
          <div className="text-2xl font-bold text-red-400" data-testid="at-risk-count">{data.atRisk}</div>
          <div className="text-xs text-red-300/70">At Risk</div>
        </div>
        <div className="text-center p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
          <div className="text-2xl font-bold text-emerald-400" data-testid="scale-count">{data.scaleCandidates}</div>
          <div className="text-xs text-emerald-300/70">Scale Ready</div>
        </div>
        <div className="text-center p-3 bg-slate-800/50 rounded-lg border border-slate-700">
          <div className="text-2xl font-bold text-slate-300" data-testid="active-count">{data.active}</div>
          <div className="text-xs text-slate-400">Active</div>
        </div>
      </div>
    </div>
  );
};

// Dedicated Campaign Health Page
const CampaignHealthPage = () => {
  const [atRisk, setAtRisk] = useState([]);
  const [scaleCandidates, setScaleCandidates] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [riskRes, scaleRes, campaignsRes] = await Promise.all([
          axios.get(`${API}/performance/campaigns/at-risk`),
          axios.get(`${API}/performance/campaigns/scale-candidates`),
          // `days_active` is a snapshot count computed live from performance_snapshots
          // (see backend/routers/performance.py::api_list_campaigns) — a campaign only
          // appears here at all if it currently has snapshot data. at-risk/scale-candidate
          // records come from a periodically-recomputed status collection and can outlive
          // the underlying snapshots, so this list is the reliable "has data" signal.
          axios.get(`${API}/performance/campaigns`),
        ]);
        setAtRisk(riskRes.data.campaigns || []);
        setScaleCandidates(scaleRes.data.campaigns || []);

        // Only request a diagnosis for campaigns that actually have snapshot data —
        // requesting one for a snapshot-less campaign always 404s (NEW-22).
        const campaignsWithSnapshots = new Set(
          (campaignsRes.data || [])
            .filter(c => (c.days_active || 0) > 0)
            .map(c => c.campaign_id)
        );

        const allCampaigns = [...(riskRes.data.campaigns || []), ...(scaleRes.data.campaigns || [])];
        const ids = [...new Set(allCampaigns.map(c => c.campaign_id))]
          .filter(id => campaignsWithSnapshots.has(id));
        const diagResults = await Promise.all(
          ids.map(id => axios.get(`${API}/performance/campaigns/diagnosis/${id}`).catch(() => null))
        );
        setDiagnoses(diagResults.filter(r => r?.data).map(r => r.data));
      } catch (e) {
        console.error('Failed to load campaign health:', e);
      } finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500" />
    </div>
  );

  // Collect all failure diagnoses
  const failureDiagnoses = diagnoses.filter(d => d.failure_mode === 'CREATIVE_FAILING' || d.failure_mode === 'BACKEND_FAILING');

  return (
    <div className="space-y-8" data-testid="campaign-health-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Campaign Health</h2>
          <p className="text-slate-400 mt-1">Monitor campaign performance and health signals</p>
        </div>
        <a
          href={`${API}/export/campaigns`}
          className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-300 hover:text-white hover:border-slate-600 transition-colors"
          data-testid="export-campaigns-csv"
        >
          Export CSV
        </a>
      </div>

      {/* At Risk Section */}
      <div>
        <h3 className="text-lg font-semibold text-red-400 mb-4 flex items-center gap-2">
          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          At Risk ({atRisk.length})
        </h3>
        {atRisk.length === 0 ? (
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 text-center text-slate-500">
            No campaigns currently at risk
          </div>
        ) : (
          <div className="space-y-4">
            {atRisk.map(camp => (
              <div key={camp.campaign_id} className="bg-slate-900/50 border border-red-500/30 rounded-xl p-6" data-testid={`at-risk-${camp.campaign_id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="text-white font-semibold text-lg">{camp.campaign_name}</h4>
                    <div className="flex items-center gap-3 mt-1 text-sm text-slate-400">
                      {camp.operator_id && <span>Op: {camp.operator_id}</span>}
                      <span className="text-red-400">{camp.consecutive_deterioration_days} consecutive days deteriorating</span>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-lg text-sm font-medium">AT RISK</span>
                </div>

                {/* Deteriorating Metrics */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                  {camp.deterioration_details?.slice(-1).map((d, i) => (
                    <React.Fragment key={i}>
                      <MetricDelta label="CPA" current={d.cpa} prev={d.cpa_prev} unit="$" bad="up" />
                      <MetricDelta label="CPC" current={d.cpc} prev={d.cpc_prev} unit="$" bad="up" />
                      <MetricDelta label="CPM" current={d.cpm} prev={d.cpm_prev} unit="$" bad="up" />
                    </React.Fragment>
                  ))}
                </div>

                {/* Recommendation */}
                <div className={`p-4 rounded-lg ${camp.recommendation === 'ISOLATE_SEGMENT' ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-red-500/10 border border-red-500/20'}`}>
                  {camp.recommendation === 'ISOLATE_SEGMENT' && camp.outperforming_segment ? (
                    <div data-testid="isolate-segment-callout">
                      <p className="text-amber-400 font-medium mb-1">Outperforming Segment Found</p>
                      <p className="text-amber-300/80 text-sm">
                        <span className="font-semibold">{camp.outperforming_segment.segment_name}</span> has CPA ${camp.outperforming_segment.avg_cpa?.toFixed(2)} vs campaign avg — consider isolating this segment before killing the campaign.
                      </p>
                    </div>
                  ) : (
                    <p className="text-red-400 text-sm">{camp.recommendation_detail}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Scale Candidates Section */}
      <div>
        <h3 className="text-lg font-semibold text-emerald-400 mb-4 flex items-center gap-2">
          <span className="w-2 h-2 bg-emerald-500 rounded-full" />
          Scale Candidates ({scaleCandidates.length})
        </h3>
        {scaleCandidates.length === 0 ? (
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 text-center text-slate-500">
            No scale candidates detected
          </div>
        ) : (
          <div className="space-y-4">
            {scaleCandidates.map(camp => (
              <div key={camp.campaign_id} className="bg-slate-900/50 border border-emerald-500/30 rounded-xl p-6" data-testid={`scale-${camp.campaign_id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="text-white font-semibold">{camp.campaign_name}</h4>
                    <p className="text-sm text-slate-400 mt-1">
                      {camp.consecutive_improvement_days} consecutive days improving
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-lg text-sm font-medium">SCALE READY</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3">
                  <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                    <div className="text-sm text-slate-400">CPA</div>
                    <div className="text-emerald-400 font-bold">${camp.latest_cpa?.toFixed(2)}</div>
                    <div className="text-xs text-emerald-300/60">trending down</div>
                  </div>
                  <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                    <div className="text-sm text-slate-400">CTR</div>
                    <div className="text-emerald-400 font-bold">{camp.latest_ctr?.toFixed(2)}%</div>
                    <div className="text-xs text-emerald-300/60">holding/improving</div>
                  </div>
                  <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                    <div className="text-sm text-slate-400">Conversions</div>
                    <div className="text-emerald-400 font-bold">{camp.latest_conversions}</div>
                    <div className="text-xs text-emerald-300/60">volume up</div>
                  </div>
                </div>
                <p className="text-emerald-300/80 text-sm">{camp.recommendation_detail}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Failure Diagnosis Section */}
      <div>
        <h3 className="text-lg font-semibold text-amber-400 mb-4 flex items-center gap-2">
          <span className="w-2 h-2 bg-amber-500 rounded-full" />
          Failure Diagnosis ({failureDiagnoses.length})
        </h3>
        {failureDiagnoses.length === 0 ? (
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 text-center text-slate-500">
            No failure modes detected
          </div>
        ) : (
          <div className="space-y-4">
            {failureDiagnoses.map(d => (
              <div
                key={d.campaign_id}
                className={`bg-slate-900/50 border rounded-xl p-6 ${
                  d.failure_mode === 'CREATIVE_FAILING' ? 'border-purple-500/30' : 'border-orange-500/30'
                }`}
                data-testid={`diagnosis-${d.campaign_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <h4 className="text-white font-semibold">{d.campaign_name}</h4>
                  <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
                    d.failure_mode === 'CREATIVE_FAILING'
                      ? 'bg-purple-500/20 text-purple-400'
                      : 'bg-orange-500/20 text-orange-400'
                  }`}>
                    {d.failure_mode === 'CREATIVE_FAILING' ? 'Creative Failing' : 'Backend Failing'}
                  </span>
                </div>
                <div className="space-y-2 mb-3">
                  {d.signals?.map((signal, i) => (
                    <p key={i} className="text-slate-300 text-sm flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-slate-500 rounded-full shrink-0" />
                      {signal}
                    </p>
                  ))}
                </div>
                <div className={`p-3 rounded-lg ${
                  d.failure_mode === 'CREATIVE_FAILING' ? 'bg-purple-500/10' : 'bg-orange-500/10'
                }`}>
                  <p className={`text-sm font-medium ${
                    d.failure_mode === 'CREATIVE_FAILING' ? 'text-purple-300' : 'text-orange-300'
                  }`}>
                    Action: {d.recommended_action}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Metric delta card
const MetricDelta = ({ label, current, prev, unit = '', bad = 'up' }) => {
  const delta = current - prev;
  const pct = prev > 0 ? ((delta / prev) * 100).toFixed(1) : '0.0';
  const isDeterioring = bad === 'up' ? delta > 0 : delta < 0;

  return (
    <div className="text-center p-3 bg-slate-800/50 rounded-lg">
      <div className="text-sm text-slate-400 mb-1">{label}</div>
      <div className={`text-lg font-bold ${isDeterioring ? 'text-red-400' : 'text-emerald-400'}`}>
        {unit}{current?.toFixed(2)}
      </div>
      <div className={`text-xs flex items-center justify-center gap-1 ${isDeterioring ? 'text-red-300/70' : 'text-emerald-300/70'}`}>
        <span>{isDeterioring ? '↑' : '↓'}</span>
        <span>{pct}%</span>
      </div>
    </div>
  );
};

export default CampaignHealthPage;
